import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import './Primitives.css';

const DEFAULT_TARGET = '#main';

/**
 * SkipToMain — keyboard-first link that lets users jump past site
 * chrome (nav, sidebar) to the main content. Visible only when focused.
 *
 * Place once per layout, near the top of <body>.
 */
const SkipToMain = ({ targetId = DEFAULT_TARGET, label }) => {
  const { t } = useTranslation();
  const href = targetId.startsWith('#') ? targetId : `#${targetId}`;

  const handleClick = (event) => {
    if (typeof document === 'undefined') return;
    const idFromHref = href.slice(1);
    // Prefer the explicit target id, then any page-provided main, then the route anchor.
    const target =
      document.getElementById(idFromHref) ||
      document.querySelector('main#main') ||
      document.querySelector('main') ||
      document.querySelector('[data-route-anchor="true"]');
    if (target) {
      event.preventDefault();
      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
      }
      target.focus({ preventScroll: false });
      target.scrollIntoView({ block: 'start' });
    }
  };

  return (
    <a className="skip-to-main" href={href} onClick={handleClick}>
      {label ?? t('a11y.skipToMain', 'Skip to main content')}
    </a>
  );
};

SkipToMain.propTypes = {
  targetId: PropTypes.string,
  label: PropTypes.string,
};

export default SkipToMain;
